import { Drawer } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";
import DetailCard from "../../atoms/DetailCard/DetailCard";
import Input from "../../atoms/Input/Input";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getAllRolesAPI } from "../../../Redux/Apis/roleApi";
import { image } from "../../../app/utils/common/image";
import ErrorText from "../../atoms/Error/errorText";
import { showErrorToast } from "../../../app/utils/common/Toast";
import {
  validateEmail,
  validatePhoneNumber,
  validateDailyHoursNotifyEmailList,
  dailyHoursNotifyEmailsDiffer,
  splitDailyHoursNotifyEmails,
} from "../../../app/utils/common/Functions";
import { DAILY_HOURS_NOTIFY_RECIPIENT_OPTIONS } from "../../../app/utils/constants/dailyHoursNotifyRecipients";
import {
  createEmployee,
  updateEmployee,
  uploadFile,
} from "../../../Redux/Apis/employeeApi";
import moment from "moment/moment";
import Loader from "../../atoms/Loader/Loader";
import { DEFAULT_PROFILE_IMAGE } from "../../../app/utils/common/baseUrl";
import {
  FINANCE_SALARY_COMPANIES_UPDATED_EVENT,
  getFinanceSalaryCompanyOptions,
} from "../../../app/utils/company/financeSalaryCompaniesStorage";
const { Option } = Select;

const EmployeeDetailDrawer = ({
  profile,
  isEditOpen,
  toggleDetailDrawer,
  isCreate = true,
}) => {
  const { roles, isRoleUpdated } = useSelector((state) => state.role);
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const [showError, setShowError] = useState(false);
  const [fieldApiErrors, setFieldApiErrors] = useState({});
  const [formApiError, setFormApiError] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const getRoleName = (id) => {
    let role = roles?.filter((role) => role.roleId === id);
    if (role.length > 0) return role[0]?.roleName;
    return "";
  };
  const [newProfilePhoto, setNewProfilePhoto] = useState();
  const buildProfileState = (sourceProfile) => ({
    firstname: sourceProfile?.firstname ?? "",
    lastname: sourceProfile?.lastname ?? "",
    email: sourceProfile?.email ?? "",
    password: isCreate ? sourceProfile?.password ?? "" : "",
    phoneNumber: sourceProfile?.phoneNumber ?? "",
    role: sourceProfile?.roleId ?? "",
    roleName: sourceProfile?.role ?? "",
    birthDate: sourceProfile?.dob ? new Date(sourceProfile?.dob) : "",
    workAnniversaryDate: sourceProfile?.workAnniversaryDate
      ? new Date(sourceProfile.workAnniversaryDate)
      : null,
    dailyWorkRequiredFrom: sourceProfile?.dailyWorkRequiredFrom
      ? new Date(sourceProfile.dailyWorkRequiredFrom)
      : sourceProfile?.workAnniversaryDate
        ? new Date(sourceProfile.workAnniversaryDate)
        : null,
    defaultWorkLocation: sourceProfile?.defaultWorkLocation || "",
    dailyHoursNotifyEmail: sourceProfile?.dailyHoursNotifyEmail ?? "",
    employeeCode: sourceProfile?.employeeCode ?? "",
    designation: sourceProfile?.designation ?? "",
    salaryCompanyId: sourceProfile?.salaryCompanyId
      ? String(sourceProfile.salaryCompanyId)
      : "",
    profile: sourceProfile?.profileImage ?? "",
  });
  const [newProfile, setNewProfile] = useState(buildProfileState(profile));
  const [companyOptions, setCompanyOptions] = useState([]);

  const dailyHoursNotifySelectOptions = useMemo(() => {
    const saved = splitDailyHoursNotifyEmails(newProfile.dailyHoursNotifyEmail);
    const base = DAILY_HOURS_NOTIFY_RECIPIENT_OPTIONS;
    const extra = saved
      .filter(
        (e) => !base.some((o) => o.value.toLowerCase() === e.toLowerCase())
      )
      .map((e) => ({ label: e, value: e }));
    return [...base, ...extra];
  }, [newProfile.dailyHoursNotifyEmail]);

  useEffect(() => {
    if (isRoleUpdated) dispatch(getAllRolesAPI());
  }, []);

  const loadCompanyOptions = useCallback(async () => {
    try {
      const options = await getFinanceSalaryCompanyOptions();
      setCompanyOptions(options);
    } catch (error) {
      showErrorToast(
        error?.response?.data?.error?.message || "Failed to load salary companies."
      );
      setCompanyOptions([]);
    }
  }, []);

  useEffect(() => {
    if (isEditOpen) {
      loadCompanyOptions();
    }
  }, [isEditOpen, loadCompanyOptions]);

  useEffect(() => {
    const refreshOptions = () => {
      loadCompanyOptions();
    };
    window.addEventListener(FINANCE_SALARY_COMPANIES_UPDATED_EVENT, refreshOptions);
    return () => window.removeEventListener(FINANCE_SALARY_COMPANIES_UPDATED_EVENT, refreshOptions);
  }, [loadCompanyOptions]);

  useEffect(() => {
    if (!isEditOpen || !companyOptions.length) return;
    const defaultValue = companyOptions[0]?.value ?? "";
    setNewProfile((current) => {
      if (String(current.salaryCompanyId || "").trim()) return current;
      return { ...current, salaryCompanyId: defaultValue };
    });
  }, [companyOptions, isEditOpen]);

  useEffect(() => {
    if (isEditOpen) {
      setNewProfile(buildProfileState(profile));
      setNewProfilePhoto(undefined);
      setShowError(false);
      setShowPassword(false);
      setFieldApiErrors({});
      setFormApiError("");
    }
  }, [profile, isEditOpen]);

  useEffect(() => {
    if (!isCreate && isDataUpdated()) {
      setIsUpdated(true);
    } else {
      setIsUpdated(false);
    }
  }, [JSON.stringify(newProfile), newProfilePhoto]);

  const onChangeData = (key, value) => {
    setFieldApiErrors((prev) => ({ ...prev, [key]: "" }));
    setFormApiError("");
    setNewProfile((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "workAnniversaryDate") {
        const prevHire = prev.workAnniversaryDate
          ? moment(prev.workAnniversaryDate).format("YYYY-MM-DD")
          : "";
        const prevRequired = prev.dailyWorkRequiredFrom
          ? moment(prev.dailyWorkRequiredFrom).format("YYYY-MM-DD")
          : "";
        if (!prevRequired || prevRequired === prevHire) {
          next.dailyWorkRequiredFrom = value;
        }
      }
      return next;
    });
  };

  const mapApiErrorToField = (apiError) => {
    const message = String(apiError?.message || "").toLowerCase();
    if (message.includes("firstname")) return { firstname: apiError?.message };
    if (message.includes("lastname")) return { lastname: apiError?.message };
    if (
      message.includes("daily hours notify") ||
      message.includes("dailyhoursnotify") ||
      message.includes("daily hours notifications") ||
      message.includes("notification emails") ||
      (message.includes("notify") && message.includes("email"))
    ) {
      return { dailyHoursNotifyEmail: apiError?.message };
    }
    if (message.includes("email")) return { email: apiError?.message };
    if (message.includes("employee code")) return { employeeCode: apiError?.message };
    if (message.includes("salary company")) return { salaryCompanyId: apiError?.message };
    if (
      message.includes("phonenumber") ||
      message.includes("phone number") ||
      message.includes("countrycode") ||
      message.includes("country code")
    ) {
      return { phoneNumber: apiError?.message };
    }
    if (message.includes("password")) return { password: apiError?.message };
    if (message.includes("role")) return { role: apiError?.message };
    if (
      message.includes("dob") ||
      message.includes("date of birth") ||
      (message.includes("date") && !message.includes("anniversary"))
    ) {
      return { birthDate: apiError?.message };
    }
    if (message.includes("anniversary") || message.includes("workanniversary")) {
      return { workAnniversaryDate: apiError?.message };
    }
    if (
      message.includes("daily work required") ||
      message.includes("dailyworkrequiredfrom") ||
      message.includes("required-from")
    ) {
      return { dailyWorkRequiredFrom: apiError?.message };
    }
    return {};
  };
  const isDataUpdated = () => {
    if (
      newProfile.firstname !== profile.firstname ||
      newProfile.lastname !== profile.lastname ||
      newProfile.email !== profile.email ||
      (newProfile.password.trim().length > 0 &&
        newProfile.password !== profile.password) ||
      newProfile.phoneNumber !== profile.phoneNumber ||
      newProfile.birthDate.toString() !==
        ((profile.dob ?? "").toString().length === 0
          ? ""
          : new Date(profile.dob).toString()) ||
      newProfile.role !== profile.roleId ||
      newProfilePhoto ||
      (() => {
        const prevWa = profile?.workAnniversaryDate
          ? moment(profile.workAnniversaryDate).format("YYYY-MM-DD")
          : "";
        const newWa = newProfile.workAnniversaryDate
          ? moment(newProfile.workAnniversaryDate).format("YYYY-MM-DD")
          : "";
        return newWa !== prevWa;
      })() ||
      (() => {
        const prevRequired = profile?.dailyWorkRequiredFrom
          ? moment(profile.dailyWorkRequiredFrom).format("YYYY-MM-DD")
          : profile?.workAnniversaryDate
            ? moment(profile.workAnniversaryDate).format("YYYY-MM-DD")
            : "";
        const newRequired = newProfile.dailyWorkRequiredFrom
          ? moment(newProfile.dailyWorkRequiredFrom).format("YYYY-MM-DD")
          : "";
        return newRequired !== prevRequired;
      })() ||
      (newProfile.defaultWorkLocation || "") !== (profile?.defaultWorkLocation || "") ||
      dailyHoursNotifyEmailsDiffer(newProfile.dailyHoursNotifyEmail, profile?.dailyHoursNotifyEmail) ||
      (newProfile.employeeCode || "").trim() !== (profile?.employeeCode || "").trim() ||
      (newProfile.designation || "").trim() !== (profile?.designation || "").trim() ||
      String(newProfile.salaryCompanyId || "") !== String(profile?.salaryCompanyId || "")
    ) {
      return true;
    }
    return false;
  };
  const validateProfile = (formData) => {
    const shouldValidatePassword = isCreate || formData.password.trim().length > 0;
    if (
      !formData.firstname.trim() ||
      !formData.lastname.trim() ||
      !formData.email.trim() ||
      !validateEmail(formData.email.trim()) ||
      (shouldValidatePassword &&
        (!formData.password.trim() || formData.password.length < 8)) ||
      !formData.phoneNumber.trim() ||
      !validatePhoneNumber(formData.phoneNumber) ||
      !formData.role ||
      !formData.roleName.trim() ||
      !formData.birthDate ||
      isNaN(Date.parse(formData.birthDate)) ||
      new Date(formData.birthDate) >= new Date()
    ) {
      return false;
    }
    if (
      formData.workAnniversaryDate &&
      (isNaN(Date.parse(formData.workAnniversaryDate)) ||
        !moment(formData.workAnniversaryDate).isValid())
    ) {
      return false;
    }
    if (
      formData.dailyWorkRequiredFrom &&
      (isNaN(Date.parse(formData.dailyWorkRequiredFrom)) ||
        !moment(formData.dailyWorkRequiredFrom).isValid())
    ) {
      return false;
    }
    if (!validateDailyHoursNotifyEmailList(formData.dailyHoursNotifyEmail)) {
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    setFieldApiErrors({});
    setFormApiError("");
    if (!validateProfile(newProfile)) {
      setShowError(true);
      return;
    }
    if (isCreate) {
      let payload = {
        firstname: newProfile.firstname,
        lastname: newProfile.lastname,
        countryCode: "+91",
        phoneNumber: newProfile.phoneNumber,
        email: newProfile.email,
        password: newProfile.password,
        roleId: newProfile.role,
        dob: moment(newProfile.birthDate).format("YYYY-MM-DD"),
      };
      if (newProfile.workAnniversaryDate) {
        payload.workAnniversaryDate = moment(newProfile.workAnniversaryDate).format("YYYY-MM-DD");
      }
      if (newProfile.dailyWorkRequiredFrom) {
        payload.dailyWorkRequiredFrom = moment(newProfile.dailyWorkRequiredFrom).format("YYYY-MM-DD");
      } else if (newProfile.workAnniversaryDate) {
        payload.dailyWorkRequiredFrom = moment(newProfile.workAnniversaryDate).format("YYYY-MM-DD");
      }
      if (
        newProfile.defaultWorkLocation === "wfh" ||
        newProfile.defaultWorkLocation === "wfs" ||
        newProfile.defaultWorkLocation === "wfo"
      ) {
        payload.defaultWorkLocation = newProfile.defaultWorkLocation;
      }
      const notifyCreate = String(newProfile.dailyHoursNotifyEmail || "").trim();
      if (notifyCreate) {
        payload.dailyHoursNotifyEmail = notifyCreate;
      }
      const employeeCodeCreate = String(newProfile.employeeCode || "").trim();
      if (employeeCodeCreate) {
        payload.employeeCode = employeeCodeCreate;
      }
      const designationCreate = String(newProfile.designation || "").trim();
      if (designationCreate) {
        payload.designation = designationCreate;
      }
      const salaryCompanyCreate = String(newProfile.salaryCompanyId || "").trim();
      if (salaryCompanyCreate) {
        payload.salaryCompanyId = salaryCompanyCreate;
      }
      if (newProfilePhoto) {
        const formData = new FormData();
        formData.append("folderName", "profile");
        formData.append("files", newProfilePhoto);
        const res = await dispatch(uploadFile(formData));
        if (uploadFile.rejected.match(res)) {
          setFormApiError("Unable to upload profile image. Please try again.");
          return;
        }
        payload.profileImage = res.payload.data[0];
      } else {
        payload.profileImage = DEFAULT_PROFILE_IMAGE;
      }
      setLoading(true);
      const response = await dispatch(createEmployee(payload));
      setLoading(false);
      if (createEmployee.fulfilled.match(response)) {
        toggleDetailDrawer();
      } else {
        const apiError = response?.payload || {};
        const mapped = mapApiErrorToField(apiError);
        if (Object.keys(mapped).length > 0) {
          setFieldApiErrors(mapped);
        } else {
          setFormApiError(
            apiError?.message || "Unable to save employee details. Please try again."
          );
        }
      }
    } else {
      let payload = {};
      if (newProfilePhoto) {
        const formData = new FormData();
        formData.append("folderName", "profile");
        formData.append("files", newProfilePhoto);
        const res = await dispatch(uploadFile(formData));
        if (uploadFile.rejected.match(res)) {
          setFormApiError("Unable to upload profile image. Please try again.");
          return;
        }
        payload.profileImage = res.payload.data[0];
      }
      if (newProfile.firstname !== profile.firstname)
        payload.firstname = newProfile.firstname;
      if (newProfile.lastname !== profile.lastname)
        payload.lastname = newProfile.lastname;
      if (newProfile.email !== profile.email) payload.email = newProfile.email;
      if (
        newProfile.password.trim().length > 0 &&
        newProfile.password !== profile.password
      )
        payload.password = newProfile.password;
      if (newProfile.phoneNumber !== profile.phoneNumber)
        payload.phoneNumber = newProfile.phoneNumber;
      if (newProfile.phoneNumber !== profile.phoneNumber)
        payload.countryCode = profile?.countryCode || "+91";
      if (newProfile.role !== profile.roleId) payload.roleId = newProfile.role;
      if (
        newProfile.birthDate.toString() !==
        ((profile.dob ?? "").toString().length === 0
          ? ""
          : new Date(profile.dob).toString())
      )
        payload.dob = moment(newProfile.birthDate).format("YYYY-MM-DD");

      const prevWa = profile?.workAnniversaryDate
        ? moment(profile.workAnniversaryDate).format("YYYY-MM-DD")
        : "";
      const newWa = newProfile.workAnniversaryDate
        ? moment(newProfile.workAnniversaryDate).format("YYYY-MM-DD")
        : "";
      if (newWa !== prevWa) {
        payload.workAnniversaryDate = newWa === "" ? "" : newWa;
      }
      const prevRequired = profile?.dailyWorkRequiredFrom
        ? moment(profile.dailyWorkRequiredFrom).format("YYYY-MM-DD")
        : profile?.workAnniversaryDate
          ? moment(profile.workAnniversaryDate).format("YYYY-MM-DD")
          : "";
      const newRequired = newProfile.dailyWorkRequiredFrom
        ? moment(newProfile.dailyWorkRequiredFrom).format("YYYY-MM-DD")
        : "";
      if (newRequired !== prevRequired) {
        payload.dailyWorkRequiredFrom = newRequired === "" ? "" : newRequired;
      }
      const prevWl = profile?.defaultWorkLocation || "";
      const newWl = newProfile.defaultWorkLocation || "";
      if (newWl !== prevWl) {
        payload.defaultWorkLocation = newWl === "" ? "" : newWl;
      }
      const prevEmployeeCode = (profile?.employeeCode || "").trim();
      const newEmployeeCode = (newProfile.employeeCode || "").trim();
      if (newEmployeeCode !== prevEmployeeCode) {
        payload.employeeCode = newEmployeeCode;
      }
      const prevDesignation = (profile?.designation || "").trim();
      const newDesignation = (newProfile.designation || "").trim();
      if (newDesignation !== prevDesignation) {
        payload.designation = newDesignation;
      }
      const prevSalaryCompany = String(profile?.salaryCompanyId || "");
      const newSalaryCompany = String(newProfile.salaryCompanyId || "");
      if (newSalaryCompany !== prevSalaryCompany) {
        payload.salaryCompanyId = newSalaryCompany;
      }
      if (dailyHoursNotifyEmailsDiffer(newProfile.dailyHoursNotifyEmail, profile?.dailyHoursNotifyEmail)) {
        const n = String(newProfile.dailyHoursNotifyEmail || "").trim();
        payload.dailyHoursNotifyEmail = n === "" ? "" : newProfile.dailyHoursNotifyEmail.trim();
      }

      if (Object.keys(payload).length > 0) {
        setLoading(true);
        const response = await dispatch(updateEmployee({ id: profile.userId, payload }));
        setLoading(false);
        if (updateEmployee.fulfilled.match(response)) {
          toggleDetailDrawer();
        } else {
          const apiError = response?.payload || {};
          const mapped = mapApiErrorToField(apiError);
          if (Object.keys(mapped).length > 0) {
            setFieldApiErrors(mapped);
          } else {
            setFormApiError(
              apiError?.message || "Unable to update employee details. Please try again."
            );
          }
        }
      }
    }
  };
  return (
    <div>
      <Drawer
        open={isEditOpen}
        onClose={toggleDetailDrawer}
        placement={window.innerWidth > 700 ? "right" : "bottom"}
        className="drawer employee-detail-drawer"
        title="Employee Details"
        width="400px"
      >
        <input
          type="file"
          className="hidden"
          accept="image/*"
          ref={fileRef}
          onChange={(e) => {
            if (e.target.files) {
              setNewProfilePhoto(e.target.files[0]);
            }
          }}
        />
        <div className="p-3 employee-detail-drawer">
          <div className="relative flex justify-center items-center flex-col gap-2">
            <img
              className="rounded-full w-32 h-32 shadow-md"
              src={
                newProfilePhoto
                  ? URL.createObjectURL(newProfilePhoto)
                  : newProfile.profile
                  ? newProfile.profile
                  : DEFAULT_PROFILE_IMAGE
              }
              alt="profile"
            />
            <div
              className=" absolute bottom-0 right-[28%] bg-white cursor-pointer p-2   border-2 rounded-full"
              onClick={() => {
                fileRef.current?.click();
              }}
            >
              <img className="h-[21px] w-[23px]" src={image.CAMERA_ICON} />
            </div>
          </div>
          <div className="flex flex-col gap-4 mt-8">
            <div>
              <Input
                label="First Name"
                classNameInput="employee-detail-input"
                value={newProfile.firstname}
                onChange={(e) => {
                  onChangeData("firstname", e.target.value);
                }}
              />
              <ErrorText
                message={"Please provide valid first name."}
                show={showError && newProfile.firstname.trim().length === 0}
              />
              {!!fieldApiErrors.firstname && (
                <ErrorText message={fieldApiErrors.firstname} show={true} />
              )}
            </div>
            <div>
              <Input
                label="Last Name"
                classNameInput="employee-detail-input"
                value={newProfile.lastname}
                onChange={(e) => {
                  onChangeData("lastname", e.target.value);
                }}
              />
              <ErrorText
                message={"Please provide valid last name."}
                show={showError && newProfile.lastname.trim().length === 0}
              />
              {!!fieldApiErrors.lastname && (
                <ErrorText message={fieldApiErrors.lastname} show={true} />
              )}
            </div>
            <div>
              <Input
                label="Employee Code"
                classNameInput="employee-detail-input"
                value={newProfile.employeeCode}
                onChange={(e) => onChangeData("employeeCode", e.target.value)}
              />
              <p className="text-xs text-[#6b7280] mt-1">
                Optional. Shown on salary slips (e.g. TCS001).
              </p>
              {!!fieldApiErrors.employeeCode && (
                <ErrorText message={fieldApiErrors.employeeCode} show={true} />
              )}
            </div>
            <div>
              <Input
                label="Designation"
                classNameInput="employee-detail-input"
                value={newProfile.designation}
                onChange={(e) => onChangeData("designation", e.target.value)}
              />
              <p className="text-xs text-[#6b7280] mt-1">
                Optional. Job title shown on salary slips (e.g. Software Developer).
              </p>
              {!!fieldApiErrors.designation && (
                <ErrorText message={fieldApiErrors.designation} show={true} />
              )}
            </div>
            <div>
              <label className="form-label">Salary company</label>
              <p className="text-xs text-[#6b7280] mb-2">
                Which company pays this employee&apos;s monthly salary. Default is{" "}
                <span className="font-medium">The Coding Studio</span>; add more companies in Finance
                Management.
              </p>
              <Select
                className="w-full h-10 employee-role-select"
                popupClassName="employee-role-select-dropdown"
                placeholder="Select company"
                options={companyOptions}
                loading={!companyOptions.length}
                value={
                  companyOptions.length
                    ? newProfile.salaryCompanyId !== undefined &&
                      newProfile.salaryCompanyId !== null
                      ? newProfile.salaryCompanyId
                      : companyOptions[0]?.value ?? ""
                    : undefined
                }
                onChange={(value) => onChangeData("salaryCompanyId", value ?? "")}
                showSearch
                optionFilterProp="label"
              />
              {!!fieldApiErrors.salaryCompanyId && (
                <ErrorText message={fieldApiErrors.salaryCompanyId} show={true} />
              )}
            </div>
            <div>
              <Input
                label="Email"
                classNameInput="employee-detail-input"
                value={newProfile.email}
                onChange={(e) => {
                  onChangeData("email", e.target.value);
                }}
              />
              <ErrorText
                message={"Please provide valid email."}
                show={showError && !validateEmail(newProfile.email.trim())}
              />
              {!!fieldApiErrors.email && (
                <ErrorText message={fieldApiErrors.email} show={true} />
              )}
            </div>
            <div>
              <label className="form-label">Daily hours notifications</label>
              <p className="text-xs text-[#6b7280] mb-2">
                Choose who gets an email when this employee submits daily hours (names only; emails are set automatically). Optional. Requires Zoho or SMTP on the server.
              </p>
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select one or more people"
                className="w-full h-10 mt-1 employee-role-select"
                popupClassName="employee-role-select-dropdown"
                options={dailyHoursNotifySelectOptions}
                value={splitDailyHoursNotifyEmails(newProfile.dailyHoursNotifyEmail)}
                onChange={(vals) =>
                  onChangeData(
                    "dailyHoursNotifyEmail",
                    Array.isArray(vals) && vals.length ? vals.join(", ") : ""
                  )
                }
                maxTagCount="responsive"
              />
              <ErrorText
                message={"Notification selection could not be validated. Clear and try again."}
                show={
                  showError &&
                  !validateDailyHoursNotifyEmailList(newProfile.dailyHoursNotifyEmail)
                }
              />
              {!!fieldApiErrors.dailyHoursNotifyEmail && (
                <ErrorText message={fieldApiErrors.dailyHoursNotifyEmail} show={true} />
              )}
            </div>
            <div>
              <Input
                label="Phone Number"
                type="text"
                classNameInput="employee-detail-input"
                value={newProfile.phoneNumber}
                onChange={(e) => {
                  if (/^\d{0,10}$/.test(e.target.value))
                    onChangeData("phoneNumber", e.target.value);
                }}
              />
              <ErrorText
                message={"Please provide valid phone number."}
                show={
                  showError &&
                  (!newProfile.phoneNumber.trim() ||
                    !validatePhoneNumber(newProfile.phoneNumber))
                }
              />
              {!!fieldApiErrors.phoneNumber && (
                <ErrorText message={fieldApiErrors.phoneNumber} show={true} />
              )}
            </div>
            <div>
              <label className="form-label">Password</label>
              <div className="flex items-center gap-2">
                <input
                  className="employee-detail-input search-input"
                  type={showPassword ? "text" : "password"}
                  placeholder={
                    isCreate
                      ? "Enter password"
                      : "Leave blank to keep current password"
                  }
                  value={newProfile.password}
                  onChange={(e) => {
                    onChangeData("password", e.target.value);
                  }}
                />
                <button
                  type="button"
                  className="attendance-save-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              <ErrorText
                message={"Password length must be greater then 8 characters."}
                show={
                  showError &&
                  (isCreate
                    ? newProfile.password.trim().length < 8
                    : newProfile.password.trim().length > 0 &&
                      newProfile.password.trim().length < 8)
                }
              />
              {!!fieldApiErrors.password && (
                <ErrorText message={fieldApiErrors.password} show={true} />
              )}
            </div>
            <div>
              <label>Role</label>
              <Select
                className="w-full h-10 mt-4 employee-role-select"
                popupClassName="employee-role-select-dropdown"
                value={newProfile?.roleName}
                onChange={(val) =>
                  setNewProfile({
                    ...newProfile,
                    role: val,
                    roleName: getRoleName(val),
                  })
                }
                placeholder="Select Role"
              >
                {roles?.map(
                  (role) =>
                    role?.roleId !== 1 && (
                      <Option value={role?.roleId}>{role?.roleName}</Option>
                    )
                )}
              </Select>
              <ErrorText
                message={"Please select role"}
                show={showError && !newProfile.role}
              />
              {!!fieldApiErrors.role && (
                <ErrorText message={fieldApiErrors.role} show={true} />
              )}
            </div>
            <div className="flex flex-col mt-5">
              <label>Birth Date</label>
              <DatePicker
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={100}
                selected={newProfile.birthDate || null}
                onChange={(date) => onChangeData("birthDate", date)}
                dateFormat="dd/MM/yyyy" // Set format to DD/MM/YYYY
                placeholderText="DD/MM/YYYY"
                maxDate={new Date(Date.now() - 24 * 60 * 60 * 1000)}
              />
            </div>
            <ErrorText
              message={"Please  provide birth date."}
              show={
                showError &&
                (!newProfile.birthDate ||
                  isNaN(Date.parse(newProfile.birthDate)) ||
                  new Date(newProfile.birthDate) >= new Date())
              }
            />
            {!!fieldApiErrors.birthDate && (
              <ErrorText message={fieldApiErrors.birthDate} show={true} />
            )}
            <div>
              <label className="form-label">Default work location</label>
              <p className="text-xs text-[#6b7280] mb-2">Used when no override exists for a day (WFH = work from home, WFS = work from site, WFO = work from office).</p>
              <Select
                className="w-full h-10 mt-1 employee-role-select"
                popupClassName="employee-role-select-dropdown"
                value={
                  newProfile.defaultWorkLocation === "wfh" ||
                    newProfile.defaultWorkLocation === "wfs" ||
                    newProfile.defaultWorkLocation === "wfo"
                    ? newProfile.defaultWorkLocation
                    : undefined
                }
                onChange={(val) => onChangeData("defaultWorkLocation", val ?? "")}
                placeholder="Not set"
                allowClear
              >
                <Option value="wfh">WFH (work from home)</Option>
                <Option value="wfs">WFS (work on site)</Option>
                <Option value="wfo">WFO (work from office)</Option>
              </Select>
            </div>
            <div className="flex flex-col mt-5">
              <label>Work anniversary (hire date)</label>
              <p className="text-xs text-[#6b7280] mt-1 mb-2">
                Optional. Used for anniversary celebrations and as the default
                start for daily work updates.
              </p>
              <DatePicker
                isClearable
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={80}
                selected={newProfile.workAnniversaryDate || null}
                onChange={(date) => onChangeData("workAnniversaryDate", date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                maxDate={new Date()}
              />
            </div>
            {!!fieldApiErrors.workAnniversaryDate && (
              <ErrorText message={fieldApiErrors.workAnniversaryDate} show={true} />
            )}
            <div className="flex flex-col mt-5">
              <label>Daily work required from</label>
              <p className="text-xs text-[#6b7280] mt-1 mb-2">
                Defaults to hire date. Set a later date to unlock daily work
                from that day without backfilling earlier updates.
              </p>
              <DatePicker
                isClearable
                showYearDropdown
                scrollableYearDropdown
                yearDropdownItemNumber={80}
                selected={newProfile.dailyWorkRequiredFrom || null}
                onChange={(date) => onChangeData("dailyWorkRequiredFrom", date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY (defaults to hire date)"
                maxDate={new Date()}
              />
            </div>
            {!!fieldApiErrors.dailyWorkRequiredFrom && (
              <ErrorText message={fieldApiErrors.dailyWorkRequiredFrom} show={true} />
            )}
            {!!formApiError && <ErrorText message={formApiError} show={true} />}
            <div>
              <div className="mt-4">
                {loading ? (
                  <div>
                    <Loader />{" "}
                  </div>
                ) : isCreate || isUpdated ? (
                  <button
                    className="bg-btn-primary px-4 py-2 text-white float-right cursor-pointer font-semibold rounded-lg"
                    onClick={onSubmit}
                  >
                    {isCreate ? "Create" : "Edit"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default EmployeeDetailDrawer;
